import { FlexRowBetween, EmptyBox, FlexColumn, FlexRow } from 'components'
import { largeScreenMode } from 'hooks'
import { DesktopMode, MobileMode } from 'layout'
import { File } from 'phosphor-react'
import React, { useState, useMemo } from 'react'
import { parseISO } from 'date-fns'
import {
    BarChart,
    Bar,
    XAxis,
    YAxis,
    CartesianGrid,
    ResponsiveContainer,
    Tooltip,
    Legend,
    LabelList,
    ReferenceLine,
    Cell,
} from 'recharts'
import { Card, CardContent, Select, Label } from 'semantic-ui-react'
import styled from 'styled-components'
import {
    ChartUtility,
    DefaultChartKeyFields,
    DateFormat,
    DateUtility,
} from 'utility'
import { AppTable } from 'components/Table'

const ChartContainer = styled.div`
    .h-500 {
        height: 600px !important;
    }
    .w-100 {
        width: 100%;
    }
`

const CustomTick = ({ x, y, payload, barWidth, maxAllowedWidth }) => {
    const text = payload.value
    const width = Math.round(barWidth / maxAllowedWidth)
    const shouldWrap = text.length > 10
    return (
        <g transform={`translate(${x},${y})`}>
            <text
                x={0}
                y={0}
                dy={16}
                textAnchor="middle"
                fill="#666"
                style={{ fontSize: '12px' }}
            >
                {shouldWrap
                    ? text
                          .match(new RegExp(`.{1,${width}}`, 'g'))
                          .map((line, index) => (
                              <tspan key={index} x={0} dy="1.5em">
                                  {line}
                              </tspan>
                          ))
                    : text}
            </text>
        </g>
    )
}

const renderCustomizedLabel = (props, ele) => {
    const { x, y, width, height, value } = props
    return (
        <g>
            <text
                x={x + width / 2}
                y={y + height / 2}
                fill="#000000"
                textAnchor="middle"
                dominantBaseline="middle"
            >
                {value && ele ? `${ele} -` : ''} {value} {value ? '%' : ''}
            </text>
        </g>
    )
}

const CardHeader = ({
    title,
    filters,
    onChange,
    hideFilter = false,
    link,
    openReport,
}) => (
    <>
        <DesktopMode>
            <FlexRowBetween>
                <span>{title}</span>
                <FlexRow className="align-items-center">
                    {Boolean(link) && (
                        <Label onClick={openReport} className="me-2 pointer">
                            <span className="align-items-center d-flex">
                                <File size={24} />{' '}
                                <p className="my-0">&nbsp;Open Report</p>
                            </span>
                        </Label>
                    )}
                    {!hideFilter && (
                        <Select
                            defaultValue={0}
                            onChange={onChange}
                            options={filters}
                        />
                    )}
                </FlexRow>
            </FlexRowBetween>
        </DesktopMode>
        <MobileMode>
            <FlexColumn>
                <span>{title}</span>
                <div className="col-12 mt-2">
                    {!hideFilter && (
                        <Select
                            defaultValue={0}
                            onChange={onChange}
                            options={filters}
                            className="col-12"
                        />
                    )}
                    {Boolean(link) && (
                        <div className="d-flex justify-content-end">
                            <Label
                                onClick={openReport}
                                className=" pointer mt-3"
                            >
                                <span className="align-items-center d-flex">
                                    <File size={20} />{' '}
                                    <span>&nbsp;Open Report</span>
                                </span>
                            </Label>
                        </div>
                    )}
                </div>
            </FlexColumn>
        </MobileMode>
    </>
)
const getTitle = value => `${value} %`

const ChartCard = ({ ele, target }) => {
    console.log('ChartCard - Received element:', ele)
    console.log('ChartCard - Initial list:', ele.list)
    const [chartList, setChartList] = useState(ele.list)
    const [link, setLink] = useState('')
    const maxAllowWidth = largeScreenMode() ? 5 : 2.6
    const barWidth = ChartUtility.calculateBarWidth(800, ele.list.length)

    const filterSectionsByFilledAndName = list => {
        return list.filter(section => {
            const sectionName = section.SectionName.trim()

            // Only return sections where filled is 0 and SectionName is not "General Result"
            return section.filled !== 0 || sectionName === 'General Result'
        })
    }
    // Apply the filter and store the result
    const filteredChartList = filterSectionsByFilledAndName(chartList)

    const onChange = (_, data) => {
        const result = data.options.find(ele => ele.value === data.value)
        setLink(result.value)
        setChartList(result.list || [])
    }
    const openReport = () => {
        window.open(
            `${window.location.origin}/app/templates/inspection/report/${link}`,
        )
    }
    return (
        <div className="col-12 h-500 mb-4">
            <Card className="w-100 h-500">
                <CardContent
                    header={
                        <CardHeader
                            title={ele.name}
                            filters={ele.filters}
                            link={link}
                            openReport={openReport}
                            onChange={onChange}
                        />
                    }
                />
                <CardContent
                    description={() => (
                        <ResponsiveContainer>
                            <BarChart data={filteredChartList}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <Tooltip />
                                <Legend />
                                <XAxis
                                    dataKey="SectionName"
                                    style={{ fontSize: '12px' }}
                                    interval={0}
                                    height={100}
                                    tick={
                                        <CustomTick
                                            barWidth={barWidth}
                                            maxAllowedWidth={maxAllowWidth}
                                        />
                                    }
                                />
                                <YAxis domain={[0, 100]} />
                                <Bar dataKey="percentage" fill="#82ca9d">
                                    <LabelList
                                        content={value =>
                                            renderCustomizedLabel(value)
                                        }
                                        formatter={getTitle}
                                    />
                                    {filteredChartList.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={
                                                entry.percentage < target
                                                    ? '#F6392D'
                                                    : '#82ca9d'
                                            }
                                        />
                                    ))}
                                </Bar>
                                <ReferenceLine y={target} stroke="#F6392D" />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                />
            </Card>
        </div>
    )
}

const ChartCardWithoutScore = ({ ele, target }) => {
    const [chartList, setChartList] = useState(ele.list)
    const [link, setLink] = useState('')
    const chartData = useMemo(
        () => ChartUtility.withoutScoreChartList(chartList || []),
        [chartList],
    )
    const maxAllowWidth = largeScreenMode() ? 5 : 2.6
    const barWidth = ChartUtility.calculateBarWidth(800, ele.list.length)

    const onChange = (_, data) => {
        const result = data.options.find(ele => ele.value === data.value)
        setLink(result.value)
        setChartList(result.withoutScoreList || [])
    }
    const openReport = () => {
        window.open(
            `${window.location.origin}/app/templates/inspection/report/${link}`,
        )
    }

    return (
        <div className="col-12 h-500 mb-4">
            <Card className="w-100 h-500">
                <CardContent
                    header={
                        <CardHeader
                            title={ele.name}
                            filters={ele.filters}
                            onChange={onChange}
                            openReport={openReport}
                        />
                    }
                />
                <CardContent
                    description={() => (
                        <ResponsiveContainer>
                            <BarChart data={chartData.list || []}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <Tooltip />
                                <Legend />
                                <XAxis
                                    dataKey="SectionName"
                                    style={{ fontSize: '12px' }}
                                    interval={0}
                                    height={100}
                                    tick={
                                        <CustomTick
                                            barWidth={barWidth}
                                            maxAllowedWidth={maxAllowWidth}
                                        />
                                    }
                                />
                                <YAxis domain={[0, 100]} />
                                {chartData.keys?.map(key => (
                                    <Bar key={key} dataKey={key} stackId="a">
                                        <LabelList
                                            dataKey={key}
                                            content={value =>
                                                renderCustomizedLabel(
                                                    value,
                                                    key,
                                                )
                                            }
                                            formatter={getTitle}
                                            position="center"
                                        />
                                        {chartData.list.map((_, i) => (
                                            <Cell
                                                key={i}
                                                fill={
                                                    key ===
                                                    DefaultChartKeyFields.unAnswered
                                                        ? '#eeeeee'
                                                        : '#82ca9d'
                                                }
                                            />
                                        ))}
                                    </Bar>
                                ))}
                                <ReferenceLine y={target} stroke="#F6392D" />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                />
            </Card>
        </div>
    )
}

export const ReportChart = ({ data, loading, target }) => {
    return (
        <ChartContainer className="row">
            {data?.map((ele, index) => {
                return (
                    <div key={ele.name + index}>
                        <ChartCard ele={ele} target={target} />
                    </div>
                )
            })}
            {!data.length && !loading && <EmptyBox />}
        </ChartContainer>
    )
}

export const ReportChartWitoutScore = ({ data, loading, target }) => {
    return (
        <ChartContainer className="row">
            {data?.map((ele, index) => {
                return (
                    <div key={ele.name + index} className="col-12">
                        <ChartCardWithoutScore ele={ele} target={target} />
                    </div>
                )
            })}
            {!data.length && !loading && <EmptyBox />}
        </ChartContainer>
    )
}

const ChartCardByNC = ({ ele }) => {
    const [chartList, setChartList] = useState(ele.list)
    const [link, setLink] = useState('')
    const chartData = useMemo(
        () => ChartUtility.byNCChartList(chartList || []),
        [chartList],
    )

    const maxAllowWidth = largeScreenMode() ? 5 : 2.6
    const barWidth = ChartUtility.calculateBarWidth(800, ele.list.length)

    const onChange = (_, data) => {
        const result = data.options.find(ele => ele.value === data.value)
        setLink(result.value)
        setChartList(result.withoutScoreList || [])
    }
    const openReport = () => {
        window.open(
            `${window.location.origin}/app/templates/inspection/report/${link}`,
        )
    }

    return chartData.length > 0 ? (
        <div className="col-12 h-500 mb-4">
            <Card className="w-100 h-500">
                <CardContent
                    header={
                        <CardHeader
                            title={ele.name}
                            filters={ele.filters}
                            onChange={onChange}
                            openReport={openReport}
                        />
                    }
                />
                <CardContent
                    description={() => (
                        <ResponsiveContainer>
                            <BarChart data={chartData}>
                                <Bar
                                    dataKey="NC"
                                    fill="#8884d8"
                                    minPointSize={2}
                                    barSize={32}
                                />
                                <Tooltip />
                                <XAxis
                                    dataKey="name"
                                    style={{ fontSize: '12px' }}
                                    tick={
                                        <CustomTick
                                            barWidth={barWidth}
                                            maxAllowedWidth={maxAllowWidth}
                                        />
                                    }
                                />
                                <YAxis />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                />
            </Card>
        </div>
    ) : null
}

const CommulativeChartCardByNC = ({ ele }) => {
    const [chartList, setChartList] = useState(ele.list)
    const [link, setLink] = useState('')

    const commulativeChartData = useMemo(
        () => ChartUtility.byCommulativeNCChartList(chartList || []),
        [chartList],
    )

    const onChange = (_, data) => {
        const result = data.options.find(ele => ele.value === data.value)
        setLink(result.value)
        setChartList(result.withoutScoreList || [])
    }
    const openReport = () => {
        window.open(
            `${window.location.origin}/app/templates/inspection/report/${link}`,
        )
    }

    const CustomTooltip = ({ active, payload }) => {
        if (active && payload && payload.length) {
            // Strip HTML tags for displaying name
            const strippedName = payload[0].payload.name.replace(
                /(<([^>]+)>)/gi,
                '',
            )
            return (
                <div
                    className="custom-tooltip"
                    style={{
                        backgroundColor: '#fff',
                        padding: '5px',
                        border: '1px solid #ccc',
                    }}
                >
                    <p>{`Name: ${strippedName}`}</p>
                    <p>{`Value: ${payload[0].value}`}</p>
                </div>
            )
        }
        return null
    }

    return commulativeChartData.length > 0 ? (
        <div className="col-12 h-500 mb-4">
            <Card className="w-100 h-500">
                <CardContent
                    header={
                        <CardHeader
                            title={ele.name}
                            filters={ele.filters}
                            onChange={onChange}
                            openReport={openReport}
                        />
                    }
                />
                <CardContent
                    description={() => (
                        <ResponsiveContainer>
                            <BarChart
                                data={commulativeChartData}
                                layout="vertical"
                            >
                                <XAxis type="number" allowDecimals={false} />
                                <Tooltip content={<CustomTooltip />} />
                                <YAxis
                                    dataKey="name"
                                    type="category"
                                    tickFormatter={name => {
                                        const strippedName = name.replace(
                                            /(<([^>]+)>)/gi,
                                            '',
                                        )
                                        const maxLength = 200 // Define max length for the name
                                        return strippedName.length > maxLength
                                            ? `${strippedName.substring(
                                                  0,
                                                  maxLength,
                                              )}...`
                                            : strippedName
                                    }}
                                    width={200}
                                />

                                <Bar dataKey="NC" minPointSize={2} barSize={32}>
                                    {commulativeChartData.map(d => {
                                        return (
                                            <Cell key={d.name} fill="#8884d8" />
                                        )
                                    })}
                                </Bar>
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                />
            </Card>
        </div>
    ) : null
}

export const ReportChartByNC = ({ data, loading }) => {
    return (
        <ChartContainer className="row">
            {data?.map((ele, index) => {
                return (
                    <div key={ele.name + index} className="col-12">
                        <ChartCardByNC ele={ele} />
                        <CommulativeChartCardByNC ele={ele} />
                    </div>
                )
            })}
            {!data.length && !loading && <EmptyBox />}
        </ChartContainer>
    )
}

export const CheckInReportDataTable = ({
    data,
    filter,
    pageChanged,
    totalCount,
}) => {
    const formatDateTime = dateTimeString => {
        if (!dateTimeString) return ''
        const date = parseISO(dateTimeString)
        return DateUtility.dateToLocalISO(date, DateFormat.dateTime)
    }

    // Flatten data for table and card
    const flatData = data.flatMap(user =>
        user.CheckInOutRecords.map(record => ({
            UserName: user.FullName,
            CheckInTime: formatDateTime(record.CheckInTime),
            CheckInPlace: record.CheckInPlace,
            CheckOutTime: formatDateTime(record.CheckOutTime),
            CheckOutPlace: record.CheckOutPlace,
            Id: `${user.FullName}-${record.CheckInTime}-${record.CheckOutTime}`,
        })),
    )

    const columns = [
        { title: 'User Name', key: 'UserName' },
        { title: 'CheckIn Date Time', key: 'CheckInTime' },
        { title: 'CheckIn Place', key: 'CheckInPlace' },
        { title: 'CheckOut Date Time', key: 'CheckOutTime' },
        { title: 'CheckOut Place', key: 'CheckOutPlace' },
    ]

    const MobileCard = ({ item }) => (
        <Card fluid>
            <Card.Content>
                <Card.Header>{item.UserName}</Card.Header>
                <Card.Meta>
                    <div>
                        <b>CheckIn:</b> {item.CheckInTime}
                    </div>
                    <div>
                        <b>CheckIn Place:</b> {item.CheckInPlace}
                    </div>
                    <div>
                        <b>CheckOut:</b> {item.CheckOutTime}
                    </div>
                    <div>
                        <b>CheckOut Place:</b> {item.CheckOutPlace}
                    </div>
                </Card.Meta>
            </Card.Content>
        </Card>
    )

    return (
        <AppTable
            data={flatData}
            columns={columns}
            filter={filter}
            pageChanged={pageChanged}
            totalCount={totalCount}
            mobileCard={<MobileCard />}
        />
    )
}

export const CheckInReportChart = ({ data, target }) => {
    // Example: Flatten the data for charting purposes
    const flattenedData = data.flatMap(user =>
        user.CheckInOutRecords.map(record => ({
            name: user.FullName,
            percentage: record.CheckOutTime && record.CheckInTime ? 100 : 0, // This is just an example
        })),
    )

    return (
        <ChartContainer className="col-12 h-500 mb-4">
            <Card className="w-100 h-500">
                <CardContent header={<h3>Check-In Report</h3>} />
                <CardContent
                    description={() => (
                        <ResponsiveContainer>
                            <BarChart data={flattenedData}>
                                <CartesianGrid strokeDasharray="3 3" />
                                <Tooltip />
                                <Legend />
                                <XAxis dataKey="name" />
                                <YAxis domain={[0, 100]} />
                                <Bar dataKey="percentage" fill="#82ca9d">
                                    <LabelList
                                        dataKey="percentage"
                                        position="top"
                                        formatter={value => `${value}%`}
                                    />
                                    {flattenedData.map((entry, index) => (
                                        <Cell
                                            key={`cell-${index}`}
                                            fill={
                                                entry.percentage < target
                                                    ? '#F6392D'
                                                    : '#82ca9d'
                                            }
                                        />
                                    ))}
                                </Bar>
                                <ReferenceLine y={target} stroke="#F6392D" />
                            </BarChart>
                        </ResponsiveContainer>
                    )}
                />
            </Card>
        </ChartContainer>
    )
}

export const CheckInReport = ({ tableData, chartData, target }) => {
    return (
        <div className="row">
            <div className="col-12 mb-4">
                <CheckInReportDataTable data={tableData} />
            </div>
            <div className="col-12">
                <CheckInReportChart data={chartData} target={target} />
            </div>
        </div>
    )
}
